import type { SwitchableModule, UserPermission } from '@repo/permissions'
import { z } from 'zod'

/* Assortment */
export const ASSORTMENTS_CSV_UPLOAD_TYPES = [
  'replace-materials-assortment',
  'replace-materials-launches',
  'edit-assortment',
] as const

export type AssortmentCsvUploadType = (typeof ASSORTMENTS_CSV_UPLOAD_TYPES)[number]

export const ASSORTMENTS_CSV_UPLOAD_TYPE_LABELS: Record<AssortmentCsvUploadType, string> = {
  'edit-assortment': 'Edit Assortment',
  'replace-materials-assortment': 'Replace Assortment Materials',
  'replace-materials-launches': 'Replace Launches Materials',
}

/* Launches */
export const LAUNCHES_CSV_UPLOAD_TYPES = [
  'add-launch-materials',
  'launches-delete-materials',
  'add-launch-erps',
  'launches-delete-erps',
] as const

export type LaunchesCsvUploadType = (typeof LAUNCHES_CSV_UPLOAD_TYPES)[number]

export const LAUNCHES_CSV_UPLOAD_TYPE_LABELS: Record<LaunchesCsvUploadType, string> = {
  'add-launch-materials': 'Add Launch Materials',
  'launches-delete-materials': 'Delete Launch Materials',
  'add-launch-erps': 'Add Launch ERPs',
  'launches-delete-erps': 'Delete Launch ERPs',
}

/* Materials */
export const MATERIALS_CSV_UPLOAD_TYPES = ['edit-materials-sequences'] as const

export type MaterialsCsvUploadType = (typeof MATERIALS_CSV_UPLOAD_TYPES)[number]

export const MATERIALS_CSV_UPLOAD_TYPE_LABELS: Record<MaterialsCsvUploadType, string> = {
  'edit-materials-sequences': 'Edit Materials Sequences',
}

/* CRT */
export const CRT_CSV_UPLOAD_TYPES = [
  'import-channel-mix-details',
  'import-product-type-variant-details',
  'import-agreement-details',
  'import-federal-excise-mappings',
  'import-retailer-rates',
  'import-consumer-rates',
  'import-cost-of-goods-details',
  'import-rap-forecasts-details',
  'import-sales-deal-actuals-details',
  'import-distribution-margin-mix-details',
  'import-budget-allocations-details',
  'import-ip-accruals-details',
  'import-calculation-run-accruals-details',
  'import-nc-provincial-contributions-details',
  'import-channel-contributions-details',
  'import-bpc-actuals-details',
  'import-retailer-margins-fmc',
  'import-retailer-margins-nc',
  'import-wholesaler-margins-fmc',
  'import-wholesaler-margins-nc',
  'import-agreement-mappings',
  'import-key-account-contract-estimates',
  'import-unreleased-materials',
] as const

export type CrtCsvUploadType = (typeof CRT_CSV_UPLOAD_TYPES)[number]

export const CRT_CSV_UPLOAD_TYPE_LABELS: Record<CrtCsvUploadType, string> = {
  'import-channel-mix-details': 'Import Channel Mix Details',
  'import-product-type-variant-details': 'Import Product Type Variant Details',
  'import-agreement-details': 'Import Agreement Details',
  'import-federal-excise-mappings': 'Import Federal Excise Mappings',
  'import-retailer-rates': 'Import Retailer Rates',
  'import-consumer-rates': 'Import Consumer Rates',
  'import-cost-of-goods-details': 'Import Cost of Goods Details',
  'import-rap-forecasts-details': 'Import RAP Forecasts Details',
  'import-sales-deal-actuals-details': 'Import Sales Deal Actuals Details',
  'import-distribution-margin-mix-details': 'Import Distribution Margins Mix Details',
  'import-budget-allocations-details': 'Import Budget Allocations Details',
  'import-ip-accruals-details': 'Import IP Accruals Details',
  'import-calculation-run-accruals-details': 'Import Calc. Run Accruals Details',
  'import-nc-provincial-contributions-details': 'Import NC Provincial Contributions Details',
  'import-channel-contributions-details': 'Import Channel Contributions Details',
  'import-bpc-actuals-details': 'Import BPC Actuals Details',
  'import-retailer-margins-fmc': 'Import Retailer Margins FMC',
  'import-retailer-margins-nc': 'Import Retailer Margins NC',
  'import-wholesaler-margins-fmc': 'Import Wholesaler Margins FMC',
  'import-wholesaler-margins-nc': 'Import Wholesaler Margins NC',
  'import-agreement-mappings': 'Import Master Agreement Mappings',
  'import-key-account-contract-estimates': 'Import Key Account Estimates',
  'import-unreleased-materials': 'Import Unreleased Materials',
}

/* Credit */
export const CREDIT_CSV_UPLOAD_TYPES = [
  'import-credit-index-scores',
  'import-google-financial-statements',
  'import-sap-customer',
  'import-sap-customer-company',
  'import-sap-partner-functions',
  'import-sap-sales-orders',
  'import-sap-billing-documents',
  'import-sap-billing-document-details',
  'import-sap-customer-transactions',
  'import-sap-customer-partner-credit-segments',
  'import-dnb',
  'import-rcl-multipliers',
  'import-province-aov-per-carton',
] as const

export type CreditCsvUploadType = (typeof CREDIT_CSV_UPLOAD_TYPES)[number]

export const CREDIT_CSV_UPLOAD_TYPE_LABELS: Record<CreditCsvUploadType, string> = {
  'import-credit-index-scores': 'Import Credit Index Scores',
  'import-google-financial-statements': 'Import Google Financial Statements',
  'import-sap-customer': 'Import SAP Customers',
  'import-sap-customer-company': 'Import SAP Customer Company Codes',
  'import-sap-partner-functions': 'Import SAP Partner Functions',
  'import-sap-sales-orders': 'Import SAP Sales Orders',
  'import-sap-billing-documents': 'Import SAP Billing Documents',
  'import-sap-billing-document-details': 'Import SAP Billing Document Details',
  'import-sap-customer-transactions': 'Import Customer Transactions (FBL5N)',
  'import-sap-customer-partner-credit-segments': 'Import Customer Partner Credit Segments',
  'import-dnb': 'Import Dun & Bradstreet Numbers',
  'import-rcl-multipliers': 'Import RCL Multipliers',
  'import-province-aov-per-carton': 'Import Province AOV per Carton',
}

/* App data */
export const APP_CSV_UPLOAD_TYPES = [
  ...ASSORTMENTS_CSV_UPLOAD_TYPES,
  ...LAUNCHES_CSV_UPLOAD_TYPES,
  ...MATERIALS_CSV_UPLOAD_TYPES,
  ...CRT_CSV_UPLOAD_TYPES,
  ...CREDIT_CSV_UPLOAD_TYPES,
] as const

export type AppCsvUploadType = (typeof APP_CSV_UPLOAD_TYPES)[number]

export const APP_CSV_UPLOAD_TYPE_LABELS: Record<AppCsvUploadType, string> = {
  ...ASSORTMENTS_CSV_UPLOAD_TYPE_LABELS,
  ...LAUNCHES_CSV_UPLOAD_TYPE_LABELS,
  ...MATERIALS_CSV_UPLOAD_TYPE_LABELS,
  ...CRT_CSV_UPLOAD_TYPE_LABELS,
  ...CREDIT_CSV_UPLOAD_TYPE_LABELS,
}

export const appCsvUploadTypesSchema = z.enum(APP_CSV_UPLOAD_TYPES)
export const appCsvHandledByWorkerUploadTypesSchema = z.enum([
  'replace-materials-assortment',
  'replace-materials-launches',
  'edit-assortment',
  'edit-materials-sequences',
  'add-launch-materials',
  'add-launch-erps',
  'import-channel-mix-details',
  'import-product-type-variant-details',
  'import-agreement-details',
  'import-federal-excise-mappings',
  'import-retailer-rates',
  'import-consumer-rates',
  'import-cost-of-goods-details',
  'import-rap-forecasts-details',
  'import-sales-deal-actuals-details',
  'import-distribution-margin-mix-details',
  'import-budget-allocations-details',
  'import-ip-accruals-details',
  'import-calculation-run-accruals-details',
  'import-nc-provincial-contributions-details',
  'import-channel-contributions-details',
  'import-credit-index-scores',
  'import-bpc-actuals-details',
  'import-google-financial-statements',
  'import-sap-customer',
  'import-sap-customer-company',
  'import-sap-partner-functions',
  'import-sap-sales-orders',
  'import-sap-billing-documents',
  'import-sap-billing-document-details',
  'import-sap-customer-transactions',
  'import-sap-customer-partner-credit-segments',
  'import-dnb',
  'import-rcl-multipliers',
  'import-province-aov-per-carton',
  'import-retailer-margins-fmc',
  'import-retailer-margins-nc',
  'import-wholesaler-margins-fmc',
  'import-wholesaler-margins-nc',
  'import-agreement-mappings',
  'import-key-account-contract-estimates',
  'import-unreleased-materials',
])

export type AppCsvHandledByWorkerUploadType = z.infer<typeof appCsvHandledByWorkerUploadTypesSchema>

export const APP_UPLOAD_TYPE_PERMISSIONS: Record<AppCsvUploadType, UserPermission> = {
  'replace-materials-assortment': 'assortment_banners_edit',
  'replace-materials-launches': 'assortment_launches_edit',
  'edit-assortment': 'assortment_banners_edit',
  'add-launch-materials': 'assortment_launches_edit',
  'add-launch-erps': 'assortment_launches_edit',
  'launches-delete-materials': 'assortment_launches_edit',
  'launches-delete-erps': 'assortment_launches_edit',
  'edit-materials-sequences': 'assortment_materials_sequences_edit',
  'import-channel-mix-details': 'crt_channel_mix_create',
  'import-product-type-variant-details': 'crt_product_type_variant_create',
  'import-agreement-details': 'crt_agreements_create',
  'import-federal-excise-mappings': 'crt_federal_excise_edit',
  'import-retailer-rates': 'crt_retailer_rates_edit',
  'import-consumer-rates': 'crt_consumer_rates_edit',
  'import-cost-of-goods-details': 'crt_cost_of_goods_create',
  'import-rap-forecasts-details': 'crt_rap_forecasts_create',
  'import-sales-deal-actuals-details': 'crt_sales_deal_actuals_create',
  'import-distribution-margin-mix-details': 'crt_distribution_margin_mix_create',
  'import-budget-allocations-details': 'crt_budget_allocations_create',
  'import-ip-accruals-details': 'crt_ip_accruals_create',
  'import-calculation-run-accruals-details': 'crt_calculation_run_accruals_create',
  'import-nc-provincial-contributions-details': 'crt_volume_forecasts_configure',
  'import-channel-contributions-details': 'crt_volume_forecasts_configure',
  'import-credit-index-scores': 'credit_index_scores_create',
  'import-bpc-actuals-details': 'crt_bpc_actuals_create',
  'import-google-financial-statements': 'google_financial_statements_create',
  'import-sap-customer': 'credit_sap_imports_create',
  'import-sap-customer-company': 'credit_sap_imports_create',
  'import-sap-partner-functions': 'credit_sap_imports_create',
  'import-sap-sales-orders': 'credit_sap_imports_create',
  'import-sap-billing-documents': 'credit_sap_imports_create',
  'import-sap-billing-document-details': 'credit_sap_imports_create',
  'import-sap-customer-transactions': 'credit_sap_imports_create',
  'import-sap-customer-partner-credit-segments': 'credit_sap_imports_create',
  'import-dnb': 'credit_duns_and_bradstreet_edit',
  'import-rcl-multipliers': 'credit_multiplier_edit',
  'import-province-aov-per-carton': 'credit_province_aov_edit',
  'import-retailer-margins-fmc': 'crt_retailer_margins_fmc_edit',
  'import-retailer-margins-nc': 'crt_retailer_margins_nc_edit',
  'import-wholesaler-margins-fmc': 'crt_wholesaler_margins_fmc_edit',
  'import-wholesaler-margins-nc': 'crt_wholesaler_margins_nc_edit',
  'import-agreement-mappings': 'crt_agreement_mappings_edit',
  'import-key-account-contract-estimates': 'crt_key_account_contract_estimates_edit',
  'import-unreleased-materials': 'crt_unreleased_materials_edit',
} as const

export const APP_UPLOAD_TYPE_S3_PATH: Record<AppCsvUploadType, string> = {
  'replace-materials-assortment': 'replace_materials_assortment',
  'replace-materials-launches': 'replace_materials_launches',
  'edit-assortment': 'edit_assortment',
  'add-launch-materials': 'add_launch_materials',
  'add-launch-erps': 'add_launch_erps',
  'launches-delete-materials': 'launches_delete_materials',
  'launches-delete-erps': 'launches_delete_erps',
  'edit-materials-sequences': 'edit_materials_sequences',
  'import-channel-mix-details': 'import_channel_mix_details',
  'import-product-type-variant-details': 'import_product_type_details', // Keep legacy S3 folder name for backward compatibility
  'import-agreement-details': 'import_agreement_details',
  'import-federal-excise-mappings': 'import_federal_excise_mappings',
  'import-retailer-rates': 'import_retailer_rates',
  'import-consumer-rates': 'import_consumer_rates',
  'import-cost-of-goods-details': 'import_cost_of_goods_details',
  'import-rap-forecasts-details': 'import_rap_forecasts_details',
  'import-sales-deal-actuals-details': 'import_sales_deal_actuals_details',
  'import-distribution-margin-mix-details': 'import_distribution_margin_mix_details',
  'import-budget-allocations-details': 'import_budget_allocations_details',
  'import-ip-accruals-details': 'import_ip_accruals_details',
  'import-calculation-run-accruals-details': 'import_calculation_run_accruals_details',
  'import-nc-provincial-contributions-details': 'import_nc_provincial_contributions_details',
  'import-channel-contributions-details': 'import_channel_contributions_details',
  'import-credit-index-scores': 'import_credit_index_scores',
  'import-bpc-actuals-details': 'import_bpc_actuals_details',
  'import-google-financial-statements': 'import_google_financial_statements',
  'import-sap-customer': 'import_sap_customer',
  'import-sap-customer-company': 'import_sap_customer_company',
  'import-sap-partner-functions': 'import_sap_partner_functions',
  'import-sap-sales-orders': 'import_sap_sales_orders',
  'import-sap-billing-documents': 'import_sap_billing_documents',
  'import-sap-billing-document-details': 'import_sap_billing_document_details',
  'import-sap-customer-transactions': 'import_sap_customer_transactions',
  'import-sap-customer-partner-credit-segments': 'import_sap_customer_partner_credit_segments',
  'import-dnb': 'import_dnb',
  'import-rcl-multipliers': 'import_rcl_multipliers',
  'import-province-aov-per-carton': 'import_province_aov_per_carton',
  'import-retailer-margins-fmc': 'import_retailer_margins_fmc',
  'import-retailer-margins-nc': 'import_retailer_margins_nc',
  'import-wholesaler-margins-fmc': 'import_wholesaler_margins_fmc',
  'import-wholesaler-margins-nc': 'import_wholesaler_margins_nc',
  'import-agreement-mappings': 'import_agreement_mappings',
  'import-key-account-contract-estimates': 'import_key_account_contract_estimates',
  'import-unreleased-materials': 'import_unreleased_materials',
} as const

/**
 * Upload types whose worker records an outcome for EVERY parsed row as `uploadsAppResults`, because the
 * report is the feature: the user reviews what happened to each row of an assortment or launch edit.
 *
 * Everything else is a streaming import (see `getStreamingImportWorker`): it writes straight into its
 * target table in bounded batches, records only the rows that FAILED, and stamps a success/failure
 * summary (see `UPLOAD_RESULT_SUMMARY_KEY`) on the upload's `attributes`. Holding a result row per line
 * does not survive a multi-million row file.
 */
export const PER_ROW_RESULT_APP_UPLOAD_TYPES = new Set<AppCsvUploadType>([
  'add-launch-materials',
  'add-launch-erps',
  'launches-delete-materials',
  'launches-delete-erps',
  'replace-materials-assortment',
  'replace-materials-launches',
  'edit-assortment',
  'edit-materials-sequences',
])

export const isErrorsOnlyUploadType = (type: string): boolean =>
  !PER_ROW_RESULT_APP_UPLOAD_TYPES.has(type as AppCsvUploadType)

/**
 * Key under which an errors-only import stashes its `UploadResultSummary` on the upload's
 * `attributes` jsonb. Read by the my-uploads report to show how many rows succeeded vs failed when
 * the successful rows are not individually stored.
 */
export const UPLOAD_RESULT_SUMMARY_KEY = 'resultSummary' as const

export type UploadResultSummary = {
  successCount: number
  failedCount: number
  totalCount: number
  /** Rows collapsed because an earlier row in the same batch carried the same key. */
  duplicateCount?: number
  /**
   * Batches whose write failed. Their rows are not persisted while every other batch is, so a
   * non-zero count is how a partially-applied import is spotted. Absent on summaries written before
   * imports were streamed.
   */
  failedBatchCount?: number
}

const CRT_CSV_UPLOAD_TYPE_SET: ReadonlySet<string> = new Set(CRT_CSV_UPLOAD_TYPES)
const CREDIT_CSV_UPLOAD_TYPE_SET: ReadonlySet<string> = new Set(CREDIT_CSV_UPLOAD_TYPES)

/**
 * Which module's `uploads_to_s3` / `uploads_app_results` an upload of this type lives in. Core is the
 * default, so the core-scope types and the assortment, launches and materials app types stay where
 * they always were.
 */
export function getUploadTypeModule(uploadType: string): SwitchableModule {
  if (CRT_CSV_UPLOAD_TYPE_SET.has(uploadType)) return 'crt'
  if (CREDIT_CSV_UPLOAD_TYPE_SET.has(uploadType)) return 'credit'
  return 'core'
}

/** `APP_UPLOAD_TYPE_S3_PATH` inverted: the S3 folder an app upload lands in names its upload type. */
export const APP_UPLOAD_S3_PATH_TO_TYPE: Record<string, AppCsvUploadType> = Object.fromEntries(
  (Object.entries(APP_UPLOAD_TYPE_S3_PATH) as [AppCsvUploadType, string][]).map(
    ([uploadType, path]) => [path, uploadType],
  ),
)

/**
 * The upload type of an app-scope S3 object, read from the first segment of its key
 * (`<APP_UPLOAD_TYPE_S3_PATH[type]>/<file name>`). `undefined` for a key that is not an upload,
 * such as an export. This is how the worker knows which module's upload log an S3 event refers to
 * before it has read anything from the database.
 */
export function getUploadTypeFromS3Key(key: string): AppCsvUploadType | undefined {
  const [folder] = key.split('/')
  return folder ? APP_UPLOAD_S3_PATH_TO_TYPE[folder] : undefined
}
